import { useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';

import config from '@occc/config';
import {
  MAP_HEIGHT,
  MAP_WIDTH,
  MIN_ZOOM,
  MAX_ZOOM,
  MAP_ZOOM,
  FALLBACK_MAP_CENTER,
} from './constants';

type InteractiveMapProps = {
  coordinates?: google.maps.LatLngLiteral | null;
  width?: string | number;
  height?: string | number;
  zoom?: number;
  draggable?: boolean;
  markerDraggable?: boolean;
  onClick?: (event: google.maps.MapMouseEvent) => void;
  onDragStart?: () => void;
  onDragEnd?: (center?: google.maps.LatLng) => void;
  onMarkerDragEnd?: (event: google.maps.MapMouseEvent) => void;
};

const apiLoaderConfig = {
  id: 'google-map-script',
  googleMapsApiKey: config.google.mapsApiKey || '',
};

export const InteractiveMap = ({
  coordinates,
  width = MAP_WIDTH,
  height = MAP_HEIGHT,
  zoom = MAP_ZOOM,
  draggable,
  markerDraggable,
  onClick,
  onDragStart,
  onDragEnd,
  onMarkerDragEnd,
}: InteractiveMapProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const { isLoaded } = useJsApiLoader(apiLoaderConfig);

  const handleLoad = (mapInstance: google.maps.Map) => {
    mapRef.current = mapInstance;

    if (!coordinates) {
      return;
    }

    mapInstance.setCenter(coordinates);
  };

  const handleDragEnd = () => {
    if (mapRef.current && onDragEnd) {
      onDragEnd(mapRef.current.getCenter());
    }
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <GoogleMap
      mapContainerStyle={{ height, width }}
      center={coordinates ?? FALLBACK_MAP_CENTER}
      zoom={zoom}
      onLoad={handleLoad}
      options={{
        minZoom: MIN_ZOOM,
        maxZoom: MAX_ZOOM,
        disableDefaultUI: true,
        gestureHandling: draggable ? undefined : 'none',
      }}
      onClick={onClick}
      onDragStart={onDragStart}
      onDragEnd={handleDragEnd}
    >
      {coordinates && (
        <Marker draggable={markerDraggable} position={coordinates} onDragEnd={onMarkerDragEnd} />
      )}
    </GoogleMap>
  );
};
